import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { CheckCircle2, XCircle, Trash2, User, GraduationCap, Send, Copy, Mail, Link2, Sparkles } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const AdminTabMarketplace = () => {
  const [tab, setTab] = useState('supervisors');
  const [supervisors, setSupervisors] = useState([]);
  const [counts, setCounts] = useState({});
  const [internships, setInternships] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [featStats, setFeatStats] = useState({});
  const [busy, setBusy] = useState(null);
  const [inviteLink, setInviteLink] = useState('');
  const [inviteEmails, setInviteEmails] = useState('');
  const [inviteNote, setInviteNote] = useState('');
  const [inviting, setInviting] = useState(false);
  const [digesting, setDigesting] = useState(false);

  const load = useCallback(async () => {
    try {
      const s = await axios.get(`${API_URL}/admin/marketplace/supervisors`);
      setSupervisors(s.data.supervisors || []);
      setCounts(s.data.counts || {});
      const i = await axios.get(`${API_URL}/admin/marketplace/internships`);
      setInternships(i.data.internships || []);
      const f = await axios.get(`${API_URL}/admin/marketplace/featured-supervisors`);
      setFeatured(f.data.supervisors || []);
      setFeatStats({ eligible: f.data.eligible_total || 0, pinned: f.data.pinned_total || 0 });
    } catch (e) {
      toast.error('Failed to load marketplace data');
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API_URL}/marketplace/invite-link`).then((r) => setInviteLink(r.data.link)).catch(() => {});
  }, []);

  const copyLink = () => { navigator.clipboard.writeText(inviteLink); toast.success('Recruiting link copied!'); };

  const sendInvites = async () => {
    const emails = inviteEmails.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
    if (emails.length === 0) { toast.error('Enter at least one email.'); return; }
    setInviting(true);
    try {
      const r = await axios.post(`${API_URL}/admin/marketplace/invite-supervisors`, { emails, personal_note: inviteNote });
      toast.success(r.data.message);
      setInviteEmails('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send invites'); }
    finally { setInviting(false); }
  };

  const sendDigest = async () => {
    setDigesting(true);
    try { await axios.post(`${API_URL}/admin/marketplace/send-daily-digest`); toast.success('Daily digest sent to admin inbox.'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to send digest'); }
    finally { setDigesting(false); }
  };

  const act = async (fn, id) => {
    setBusy(id);
    try { await fn(); await load(); } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(null); }
  };

  const verify = (p) => act(async () => {
    await axios.post(`${API_URL}/admin/marketplace/supervisors/${p.profile_id}/verify`);
    toast.success(`${p.full_name} verified`);
  }, p.profile_id);
  const reject = (p) => act(async () => {
    await axios.post(`${API_URL}/admin/marketplace/supervisors/${p.profile_id}/reject`);
    toast.success(`${p.full_name} rejected`);
  }, p.profile_id);
  const remove = (it) => act(async () => {
    await axios.post(`${API_URL}/admin/marketplace/internships/${it.internship_id}/remove`);
    toast.success('Internship removed');
  }, it.internship_id);

  const toggleFeature = (s) => act(async () => {
    await axios.post(`${API_URL}/admin/marketplace/supervisors/${s.user_id}/homepage-feature`, { featured: !s.homepage_featured });
    toast.success(s.homepage_featured ? `${s.full_name} removed from homepage` : `${s.full_name} featured on homepage`);
  }, s.user_id);
  const setRank = (s, rank) => act(async () => {
    await axios.post(`${API_URL}/admin/marketplace/supervisors/${s.user_id}/homepage-feature`, { featured: true, rank });
    toast.success('Order updated');
  }, s.user_id);

  return (
    <div className="space-y-4" data-testid="admin-marketplace-tab">
      {/* Recruiting & tools */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border rounded-xl p-5" data-testid="invite-supervisor-card">
          <h3 className="font-serif text-lg font-bold text-primary flex items-center gap-2 mb-1"><Sparkles className="w-5 h-5 text-secondary" /> Invite a Supervisor</h3>
          <p className="text-sm text-gray-500 mb-3">Recruit real LCSW-S supervisors to self-register. Share the link or email invites directly.</p>
          <div className="flex items-center gap-2 mb-3">
            <div className="flex-1 flex items-center gap-2 bg-gray-50 border rounded-lg px-3 py-2 text-sm text-gray-600 overflow-hidden">
              <Link2 className="w-4 h-4 flex-shrink-0" /> <span className="truncate" data-testid="invite-link-text">{inviteLink || '…'}</span>
            </div>
            <Button size="sm" variant="outline" onClick={copyLink} data-testid="copy-invite-link"><Copy className="w-4 h-4 mr-1" /> Copy</Button>
          </div>
          <Textarea rows={2} placeholder="Optional personal note to include in the invite email…" value={inviteNote} onChange={(e) => setInviteNote(e.target.value)} className="mb-2" data-testid="invite-note" />
          <Input placeholder="Emails (comma or space separated)" value={inviteEmails} onChange={(e) => setInviteEmails(e.target.value)} className="mb-2" data-testid="invite-emails" />
          <Button className="bg-secondary hover:bg-secondary-hover text-white" onClick={sendInvites} disabled={inviting} data-testid="send-invites-btn">
            <Mail className="w-4 h-4 mr-1" /> {inviting ? 'Sending…' : 'Send Invites'}
          </Button>
        </div>
        <div className="bg-white border rounded-xl p-5" data-testid="digest-card">
          <h3 className="font-serif text-lg font-bold text-primary flex items-center gap-2 mb-1"><Send className="w-5 h-5 text-secondary" /> Daily Digest</h3>
          <p className="text-sm text-gray-500 mb-3">Sends automatically each morning with featured jobs, new supervisor signups, and new internship signups & applicants. Send one now to preview.</p>
          <Button variant="outline" onClick={sendDigest} disabled={digesting} data-testid="send-digest-btn">
            <Send className="w-4 h-4 mr-1" /> {digesting ? 'Sending…' : 'Send Digest Now'}
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="supervisors" data-testid="mk-tab-supervisors">
            Supervisors {counts.pending ? <Badge className="ml-2 bg-amber-500 text-white">{counts.pending} pending</Badge> : null}
          </TabsTrigger>
          <TabsTrigger value="internships" data-testid="mk-tab-internships">Internships ({internships.length})</TabsTrigger>
          <TabsTrigger value="featured" data-testid="mk-tab-featured">Homepage Featured ({featStats.pinned || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="supervisors" className="space-y-3 mt-4">
          {supervisors.length === 0 && <p className="text-gray-500">No supervisor profiles yet.</p>}
          {supervisors.map((p) => (
            <div key={p.profile_id} className="bg-white border rounded-xl p-4 flex items-start justify-between gap-4" data-testid={`admin-supervisor-${p.profile_id}`}>
              <div className="flex gap-3">
                <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0"><User className="w-5 h-5 text-primary" /></div>
                <div>
                  <div className="font-semibold text-primary flex items-center flex-wrap">{p.full_name}
                    <StatusBadge status={p.verification_status} />
                    {p.membership_status === 'active' && <Badge className="ml-2 bg-blue-100 text-blue-700">{p.plan_id}</Badge>}
                  </div>
                  <p className="text-sm text-gray-600">{p.license_type} • {p.license_number || 'no #'} • {p.license_state}</p>
                  <p className="text-xs text-gray-500 mt-1">{(p.specialties || []).join(', ')}</p>
                </div>
              </div>
              {p.is_demo ? (
                <Badge className="bg-gray-200 text-gray-600 flex-shrink-0" data-testid={`admin-demo-${p.profile_id}`}>Demonstration profile — not a real supervisor</Badge>
              ) : p.verification_status !== 'verified' && (
                <div className="flex gap-2 flex-shrink-0">
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" disabled={busy === p.profile_id}
                    onClick={() => verify(p)} data-testid={`verify-supervisor-${p.profile_id}`}>
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Verify via BHEC
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600" disabled={busy === p.profile_id}
                    onClick={() => reject(p)} data-testid={`reject-supervisor-${p.profile_id}`}>
                    <XCircle className="w-4 h-4 mr-1" /> Reject
                  </Button>
                </div>
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="internships" className="space-y-3 mt-4">
          {internships.length === 0 && <p className="text-gray-500">No internships yet.</p>}
          {internships.map((it) => (
            <div key={it.internship_id} className="bg-white border rounded-xl p-4 flex items-start justify-between gap-4" data-testid={`admin-internship-${it.internship_id}`}>
              <div className="flex gap-3">
                <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0"><GraduationCap className="w-5 h-5 text-primary" /></div>
                <div>
                  <div className="font-semibold text-primary flex items-center flex-wrap">{it.title} <Badge className="ml-1 bg-gray-100 text-gray-700">{it.status}</Badge></div>
                  <p className="text-sm text-gray-600">{it.org_name} • {it.location} • {it.degree_level}</p>
                </div>
              </div>
              {it.status === 'active' && (
                <Button size="sm" variant="outline" className="text-red-600 flex-shrink-0" disabled={busy === it.internship_id}
                  onClick={() => remove(it)} data-testid={`remove-internship-${it.internship_id}`}>
                  <Trash2 className="w-4 h-4 mr-1" /> Remove
                </Button>
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="featured" className="space-y-3 mt-4">
          <p className="text-sm text-gray-500">
            {featStats.eligible || 0} verified supervisors eligible. Pinned supervisors always show first (by order); the rest rotate fairly.
            Only approved, license-verified profiles ever appear on the homepage.
          </p>
          {featured.length === 0 && <p className="text-gray-500">No eligible (verified) supervisors yet.</p>}
          {featured.map((s) => (
            <div key={s.user_id} className="bg-white border rounded-xl p-4 flex items-center justify-between gap-4" data-testid={`admin-featured-${s.user_id}`}>
              <div className="flex gap-3 items-center min-w-0">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0"><User className="w-5 h-5 text-primary" /></div>
                <div className="min-w-0">
                  <div className="font-semibold text-primary truncate">{s.full_name}</div>
                  <p className="text-xs text-gray-500">{s.license_type} • {[s.city, s.license_state].filter(Boolean).join(', ')} • {s.homepage_impressions || 0} views • {s.homepage_clicks || 0} clicks</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {s.homepage_featured && (
                  <Input type="number" min={1} defaultValue={s.homepage_rank === 999 ? '' : s.homepage_rank} placeholder="Order"
                    className="w-20 h-9" disabled={busy === s.user_id}
                    onBlur={(e) => { const v = parseInt(e.target.value, 10); if (!Number.isNaN(v)) setRank(s, v); }}
                    data-testid={`featured-rank-${s.user_id}`} />
                )}
                <Button size="sm" disabled={busy === s.user_id} onClick={() => toggleFeature(s)}
                  className={s.homepage_featured ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-secondary hover:bg-secondary-hover text-white'}
                  data-testid={`toggle-featured-${s.user_id}`}>
                  {s.homepage_featured ? 'Unfeature' : 'Feature'}
                </Button>
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    verified: 'bg-green-100 text-green-700', pending: 'bg-amber-100 text-amber-700', rejected: 'bg-red-100 text-red-700',
  };
  return <Badge className={`ml-2 ${map[status] || 'bg-gray-100 text-gray-600'}`}>{status}</Badge>;
};

export default AdminTabMarketplace;
